import { supabaseClient } from './supabase';
import { logger } from './logger';
import type { ListItem } from '../types/list';
import { HOTEL_ID } from './supabase/configClient';

class ListClient {
  private static instance: ListClient;

  private constructor() {}

  public static getInstance(): ListClient {
    if (!ListClient.instance) {
      ListClient.instance = new ListClient();
    }
    return ListClient.instance;
  }

  public async getItems(sectionId: string, sortType?: string): Promise<ListItem[]> {
    try {
      logger.debug('Starting to fetch list items', {
        category: 'API',
        data: { sectionId, hotelId: HOTEL_ID, sortType }
      });

      // Vérifier les paramètres requis
      if (!sectionId) {
        throw new Error('Section ID is required');
      }

      // Charger les items, traductions et configuration de la section en parallèle
      const [itemsResponse, translationsResponse, sectionResponse] = await Promise.all([
        supabaseClient
          .from('hotels_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId),
        
        supabaseClient
          .from('translations_section')
          .select('*')
          .eq('hotel_id', HOTEL_ID)
          .eq('section_id', sectionId),

        supabaseClient
          .from('hotels_navigation')
          .select('filter_open_section')
          .eq('id', sectionId)
          .single()
      ]);

      if (itemsResponse.error) {
        throw itemsResponse.error;
      }

      if (translationsResponse.error) {
        throw translationsResponse.error;
      }

      // Créer un map des traductions par item_id
      const translationsMap = new Map();
      translationsResponse.data.forEach(translation => {
        const translations = {
          fr: translation.fr,
          en: translation.en,
          es: translation.es,
          it: translation.it,
          pt: translation.pt,
          de: translation.de,
          nl: translation.nl,
          ja: translation.ja,
          zh: translation.zh,
          ru: translation.ru
        };

        translationsMap.set(translation.item_id, translations);

        logger.debug('Found translations for item', {
          category: 'Translation',
          data: {
            item_id: translation.item_id,
            translations: Object.entries(translations)
              .filter(([_, value]) => value)
              .reduce((acc, [key, value]) => ({
                ...acc,
                [key]: value.substring(0, 100) + (value.length > 100 ? '...' : '')
              }), {}),
            availableLanguages: Object.entries(translations)
              .filter(([_, value]) => value)
              .map(([key]) => key),
            section_id: sectionId
          }
        });
      });

      // Transform the data into ListItems with translations
      const items = itemsResponse.data.map(record => {
        // Récupérer les traductions pour cet item
        const translations = translationsMap.get(record.id) || {};

        logger.debug('Processing item with translations', {
          category: 'Translation',
          data: {
            item_id: record.id,
            item_name: record.name,
            description: record.description?.substring(0, 100) + (record.description?.length > 100 ? '...' : ''),
            has_translations: Object.keys(translations).length > 0,
            translations: Object.entries(translations)
              .filter(([_, value]) => value)
              .reduce((acc, [key, value]) => ({
                ...acc,
                [key]: value.substring(0, 100) + (value.length > 100 ? '...' : '')
              }), {}),
            section_id: sectionId
          }
        });

        return {
          id: record.id,
          name: record.name,
          descriptions: translations,
          description: record.description,
          fulladress: record.fulladress || '',
          telephone: record.telephone || '',
          url: record.url || '',
          category: record.category || 'Autre',
          type: record.type || '',
          image: record.image_metadata?.url || '',
          appstore: record.appstore || '',
          playstore: record.playstore || ''
        };
      });

      // Utiliser le tri initial de la section si aucun tri n'est spécifié
      const initialSortType = !sortType && sectionResponse.data?.filter_open_section;
      const effectiveSortType = sortType || initialSortType;

      // Sort items if needed
      const sortedItems = this.sortItems(items, effectiveSortType);

      logger.debug('Items and translations loaded successfully', {
        category: 'API',
        data: { 
          itemCount: items.length,
          translationCount: translationsResponse.data.length,
          itemsWithTranslations: items.filter(item => Object.keys(item.descriptions).length > 0).length,
          sectionId,
          sortType: effectiveSortType
        }
      });

      return sortedItems;

    } catch (error) {
      logger.error('Error fetching list items', {
        category: 'API',
        data: { error, sectionId, hotelId: HOTEL_ID }
      });
      throw error;
    }
  }

  private sortItems(items: ListItem[], sortType?: string): ListItem[] {
    switch (sortType) {
      case 'shuffle':
        return [...items].sort(() => Math.random() - 0.5);
      
      case 'name':
        return [...items].sort((a, b) => a.name.localeCompare(b.name));
      
      case 'category':
        return [...items].sort((a, b) => {
          const catCompare = a.category.localeCompare(b.category);
          if (catCompare !== 0) return catCompare;
          return a.name.localeCompare(b.name);
        });
      
      default:
        return items;
    }
  }
}

const listClient = ListClient.getInstance();

export { listClient };